import type { LicenseCheckDto, LicenseDetailDto, LicenseProduct, LicenseSummaryDto, MeResponse } from '@dbrain/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCapabilities, type Experience } from '../experience.js';
import { api } from './client.js';
import { keys, useMe } from './hooks.js';

/**
 * The enterprise license on the screens (Phase 13f, 06 §26.4): its state for everyone (the chip, the read-only
 * card, a product that is off) and, for super admins, the details and the upload. Read again every few minutes,
 * so the chip changes on the day it should; a write refused with license_read_only reads it at once (App).
 */
export const licenseKey = ['license'] as const;

export const PRODUCT_OF: Record<Experience, LicenseProduct> = { chat: 'companygpt', code: 'companycode' };

/** the firm's super admin (ROLE_APP_SUPERADMIN) or the break-glass admin, both the firm's owner in Oppy */
export const isSuperAdmin = (me: MeResponse | null | undefined) => me?.tenant.role === 'owner';

export const isDetail = (license: LicenseSummaryDto | undefined): license is LicenseDetailDto => Boolean(license && 'installationId' in license);

export function useLicense() {
  const capabilities = useCapabilities();
  const me = useMe();
  return useQuery({
    queryKey: licenseKey,
    queryFn: () => api<LicenseSummaryDto | LicenseDetailDto>('GET', '/api/license'),
    enabled: capabilities.licensing && Boolean(me.data),
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });
}

/** Whether an experience's product is on: always without licensing, and while the license loads. */
export function useProductOn(experience: Experience): boolean {
  const license = useLicense().data;
  return !license?.enabled || license.products.includes(PRODUCT_OF[experience]);
}

export function useReadOnly(): boolean {
  const license = useLicense().data;
  return Boolean(license?.enabled && license.status === 'read_only');
}

export const useLicenseCheck = () => useMutation({ mutationFn: (file: string) => api<LicenseCheckDto>('POST', '/api/license/check', { file }) });

export function useLicenseApply() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: string) => api<LicenseDetailDto>('POST', '/api/license', { file }),
    onSuccess: (detail) => {
      queryClient.setQueryData(licenseKey, detail);
      void queryClient.invalidateQueries({ queryKey: keys.notifications });
    },
  });
}
